import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import {
  getCandidateDisplayName,
  groupStatusesForMenu,
} from "../../helpers/RequirementKanbanHelper";

export default function KanbanStatusChangeModal({
  show,
  onHide,
  candidate,
  statuses = [],
  saving = false,
  onConfirm,
}) {
  const [selectedStatusId, setSelectedStatusId] = useState("");
  const [remark, setRemark] = useState("");

  const currentStatusId = String(candidate?.candidate_status_id ?? "");
  const currentStatusName = candidate?.status_name || "Unassigned";

  useEffect(() => {
    if (show) {
      setSelectedStatusId("");
      setRemark("");
    }
  }, [show, candidate?._candidate_id]);

  const menuGroups = useMemo(() => groupStatusesForMenu(statuses), [statuses]);

  const selectedStatus = useMemo(
    () => statuses.find((row) => String(row._status_id) === String(selectedStatusId)),
    [statuses, selectedStatusId]
  );

  const schedulesInterview = Number(selectedStatus?.status_schedule_interview) === 1;
  const isSameStatus = selectedStatusId && String(selectedStatusId) === currentStatusId;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!selectedStatus || isSameStatus) {
      return;
    }

    onConfirm?.({
      status: selectedStatus,
      remark: remark.trim(),
      schedulesInterview,
    });
  };

  if (!candidate) {
    return null;
  }

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" className="cms-modal-form">
      <Modal.Header closeButton>
        <Modal.Title>Change candidate status</Modal.Title>
      </Modal.Header>

      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <p className="mb-3 text-muted small">
            Update status for <strong>{getCandidateDisplayName(candidate)}</strong>. Current
            status: <strong>{currentStatusName}</strong>.
          </p>

          <div className="mb-3">
            <label htmlFor="kanban-status-select" className="form-label">
              New status
            </label>
            <select
              id="kanban-status-select"
              className="form-select"
              value={selectedStatusId}
              onChange={(event) => setSelectedStatusId(event.target.value)}
              required
            >
              <option value="">Select status…</option>
              {Object.values(menuGroups).map((group) =>
                group.items.length > 0 ? (
                  <optgroup key={group.label} label={group.label}>
                    {group.items.map((status) => (
                      <option
                        key={status._status_id}
                        value={status._status_id}
                        disabled={String(status._status_id) === currentStatusId}
                      >
                        {status.status_name}
                        {String(status._status_id) === currentStatusId ? " (current)" : ""}
                      </option>
                    ))}
                  </optgroup>
                ) : null
              )}
            </select>
          </div>

          <div className="mb-3">
            <label htmlFor="kanban-status-remark" className="form-label">
              Remark <span className="text-muted fw-normal">(optional)</span>
            </label>
            <textarea
              id="kanban-status-remark"
              className="form-control"
              rows={3}
              value={remark}
              onChange={(event) => setRemark(event.target.value)}
              placeholder="Reason or note for this status change"
            />
          </div>

          {selectedStatus && !isSameStatus ? (
            <div className="kanban-status-change-summary">
              <div className="small text-muted mb-1">You are about to move:</div>
              <div className="fw-semibold">
                {currentStatusName}
                <i className="ri-arrow-right-line mx-2" />
                {selectedStatus.status_name}
              </div>
              {schedulesInterview ? (
                <div className="small text-warning mt-2">
                  <i className="ri-calendar-event-line me-1" />
                  This status schedules an interview. You will be asked to complete scheduling
                  before the status is saved.
                </div>
              ) : null}
            </div>
          ) : null}
        </Modal.Body>

        <Modal.Footer>
          <button type="button" className="btn btn-light" onClick={onHide} disabled={saving}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving || !selectedStatus || isSameStatus}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Saving…
              </>
            ) : schedulesInterview ? (
              "Continue to schedule interview"
            ) : (
              "Confirm status change"
            )}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
